(() => {
  if (window.TwoCartSections?.initialize) {
    window.TwoCartSections.initialize(document);
    return;
  }
  const SELECTORS = {
    overlay: '[data-tc-overlay]',
    drawer: '[data-tc-cart-drawer]',
    cartForms: '[data-tc-cart-form]',
    cartCount: '[data-tc-cart-count]',
    message: '[data-tc-cart-message]'
  };

  const state = {
    lastFocus: null,
    updating: false
  };

  const routes = () => {
    const config = document.querySelector('[data-tc-routes]');
    return {
      add: config?.dataset.add || `${window.Shopify?.routes?.root || '/'}cart/add.js`,
      change: config?.dataset.change || `${window.Shopify?.routes?.root || '/'}cart/change.js`,
      update: config?.dataset.update || `${window.Shopify?.routes?.root || '/'}cart/update.js`,
      cart: config?.dataset.cart || `${window.Shopify?.routes?.root || '/'}cart.js`,
      recommendations: config?.dataset.recommendations || `${window.Shopify?.routes?.root || '/'}recommendations/products`
    };
  };

  const getOverlay = () => document.querySelector(SELECTORS.overlay);
  const getDrawer = () => document.querySelector(SELECTORS.drawer);
  const focusable = (container) => Array.from(container?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') || []).filter((element) => element.offsetParent !== null);

  function lockPage() {
    document.body.classList.add('tc-scroll-locked');
  }

  function unlockPageIfClear() {
    if (!document.querySelector('.tc-drawer.is-open, .tc-measure-panel.is-open')) {
      document.body.classList.remove('tc-scroll-locked');
      getOverlay()?.classList.remove('is-open');
    }
  }

  function openPanel(panel, trigger) {
    if (!panel) return;
    state.lastFocus = trigger || document.activeElement;
    getOverlay()?.classList.add('is-open');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    lockPage();
    requestAnimationFrame(() => focusable(panel)[0]?.focus());
  }

  function closePanel(panel) {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    unlockPageIfClear();
    state.lastFocus?.focus?.();
    state.lastFocus = null;
  }

  function closeAllPanels() {
    document.querySelectorAll('.tc-drawer.is-open, .tc-measure-panel.is-open').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    });
    unlockPageIfClear();
    state.lastFocus?.focus?.();
    state.lastFocus = null;
  }

  function trapFocus(event, panel) {
    if (event.key !== 'Tab') return;
    const items = focusable(panel);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  const variantData = (root) => {
    try { return JSON.parse(root.querySelector('[data-tc-variants-json]')?.textContent || '[]'); }
    catch (error) { return []; }
  };

  function formatMoney(cents, format) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, format);
    const currency = document.documentElement.dataset.currency || window.Shopify?.currency?.active;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: currency ? 'currency' : 'decimal',
        currency: currency || undefined,
        minimumFractionDigits: currency === 'JPY' ? 0 : 2,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  }

  function updateVariant(root, variant, label, formattedPrice, formattedCompare) {
    const picker = root.querySelector('[data-tc-variant-picker]');
    const hiddenId = root.querySelector('[data-tc-variant-id]');
    const labelNode = root.querySelector('[data-tc-variant-label]');
    const addButton = root.querySelector('[data-tc-product-submit]');
    const price = root.querySelector('[data-tc-price]');
    const compare = root.querySelector('[data-tc-compare]');
    const moneyFormat = root.dataset.moneyFormat || '';
    if (!variant || !hiddenId || !addButton) return;

    hiddenId.value = variant.id;
    if (labelNode) labelNode.textContent = label || variant.public_title || variant.title;
    picker?.classList.remove('has-error');
    root.querySelectorAll('[data-tc-variant-option]').forEach((option) => option.setAttribute('aria-selected', String(option.dataset.variantId === String(variant.id))));
    if (price) price.textContent = formattedPrice || formatMoney(variant.price, moneyFormat);
    if (compare) compare.textContent = formattedCompare ?? (variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price, moneyFormat) : '');
    addButton.disabled = !variant.available;
    addButton.textContent = variant.available ? (addButton.dataset.addText || 'Add to bag') : (addButton.dataset.soldText || 'Sold out');

    const imageId = String(variant.featured_media?.id || '');
    if (imageId) {
      const media = root.querySelector(`[data-media-id="${CSS.escape(imageId)}"]`);
      const galleryRail = media?.closest('.tc-product__gallery-rail');
      if (media && galleryRail && matchMedia('(min-width: 900px)').matches) {
        galleryRail.scrollTo({ top: media.offsetTop, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      } else {
        media?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      }
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function initProduct(root) {
    if (!root || root.dataset.tcProductReady === 'true') return;
    root.dataset.tcProductReady = 'true';
    root.tcAbortController = new AbortController();
    const picker = root.querySelector('[data-tc-variant-picker]');
    const menu = root.querySelector('[data-tc-variant-menu]');
    const pickerButton = root.querySelector('[data-tc-variant-toggle]');
    const variantIdInput = root.querySelector('[data-tc-variant-id]');
    if (variantIdInput) variantIdInput.name = variantIdInput.dataset.inputName || 'id';

    pickerButton?.addEventListener('click', () => {
      const shouldOpen = menu.hidden;
      menu.hidden = !shouldOpen;
      pickerButton.setAttribute('aria-expanded', String(shouldOpen));
    });
    pickerButton?.addEventListener('keydown', (event) => {
      if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
      event.preventDefault();
      menu.hidden = false;
      pickerButton.setAttribute('aria-expanded', 'true');
      const options = menu.querySelectorAll('[data-tc-variant-option]');
      (event.key === 'ArrowUp' ? options[options.length - 1] : options[0])?.focus();
    });
    menu?.addEventListener('keydown', (event) => {
      const options = Array.from(menu.querySelectorAll('[data-tc-variant-option]'));
      const current = options.indexOf(document.activeElement);
      if (event.key === 'Escape') {
        event.preventDefault();
        menu.hidden = true;
        pickerButton.setAttribute('aria-expanded', 'false');
        pickerButton.focus();
      } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        let next = current;
        if (event.key === 'ArrowDown') next = (current + 1) % options.length;
        if (event.key === 'ArrowUp') next = (current - 1 + options.length) % options.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = options.length - 1;
        options[next]?.focus();
      }
    });

    root.querySelectorAll('[data-tc-variant-option]').forEach((option) => {
      option.addEventListener('click', () => {
        const variant = variantData(root).find((entry) => String(entry.id) === option.dataset.variantId);
        updateVariant(root, variant, option.dataset.label, option.dataset.price, option.dataset.compare);
        menu.hidden = true;
        pickerButton.setAttribute('aria-expanded', 'false');
        pickerButton.focus();
      });
    });

    root.querySelectorAll('[data-tc-size-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        root.querySelectorAll('[data-tc-size-tab]').forEach((tab) => {
          const active = tab === button;
          tab.classList.toggle('is-active', active);
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        root.querySelectorAll('[data-tc-size-table]').forEach((table) => { table.hidden = table.dataset.tcSizeTable !== button.dataset.tcSizeTab; });
      });
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const tabs = Array.from(root.querySelectorAll('[data-tc-size-tab]'));
        const current = tabs.indexOf(button);
        let next = current;
        if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        tabs[next]?.focus();
        tabs[next]?.click();
      });
    });

    root.querySelectorAll('[data-tc-measure-open]').forEach((button) => button.addEventListener('click', (event) => {
      event.preventDefault();
      openPanel(root.querySelector('[data-tc-measure-panel]'), button);
    }));

    const productForm = root.querySelector('[data-tc-product-form]');
    productForm?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const idInput = productForm.querySelector('[data-tc-variant-id]');
      const submit = productForm.querySelector('[data-tc-product-submit]');
      const status = root.querySelector('[data-tc-product-status]');
      if (!idInput?.value) {
        picker?.classList.add('has-error');
        pickerButton?.focus();
        if (status) status.textContent = root.dataset.selectError || 'Please select an option.';
        return;
      }
      const selectedVariant = variantData(root).find((entry) => String(entry.id) === idInput.value);
      if (!selectedVariant?.available) return;
      submit.disabled = true;
      submit.classList.add('tc-loading');
      if (status) status.textContent = '';
      try {
        const response = await fetch(routes().add, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(productForm) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item.');
        submit.textContent = submit.dataset.addedText || 'Product added to bag';
        await refreshCart({ open: true, trigger: submit });
        document.dispatchEvent(new CustomEvent('two-cart:added', { detail: data }));
      } catch (error) {
        if (status) status.textContent = error.message;
        submit.disabled = false;
        submit.textContent = submit.dataset.addText || 'Add to bag';
      } finally {
        submit.classList.remove('tc-loading');
        submit.disabled = !selectedVariant.available;
      }
    });

    document.addEventListener('click', (event) => {
      if (picker && !picker.contains(event.target)) {
        menu.hidden = true;
        pickerButton?.setAttribute('aria-expanded', 'false');
      }
    }, { signal: root.tcAbortController?.signal });
  }

  async function fetchCartHTML() {
    const drawer = getDrawer();
    if (!drawer) return null;
    const sectionId = drawer.dataset.sectionId;
    if (!sectionId) return null;
    const url = new URL(window.location.href);
    url.search = '';
    url.searchParams.set('section_id', sectionId);
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Unable to refresh cart.');
    return response.text();
  }

  async function getCartJSON() {
    const response = await fetch(routes().cart, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart.');
    return response.json();
  }

  async function refreshCart({ open = false, trigger = null } = {}) {
    const drawer = getDrawer();
    const [html, cart] = await Promise.all([fetchCartHTML(), getCartJSON()]);
    if (html && drawer) {
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = documentFragment.querySelector(SELECTORS.drawer);
      const newForm = replacement?.querySelector('[data-tc-cart-form]');
      const currentForm = drawer.querySelector('[data-tc-cart-form]');
      if (newForm && currentForm) currentForm.replaceWith(newForm);
    }
    if (cart) document.querySelectorAll(SELECTORS.cartCount).forEach((node) => { node.textContent = cart.item_count; });
    if (open) openPanel(drawer, trigger);
    initRecommendations(drawer || document);
    return cart;
  }

  async function changeLine(lineElement, quantity, focusHint = '') {
    if (state.updating || !lineElement) return;
    state.updating = true;
    const form = lineElement.closest('[data-tc-cart-form]');
    form?.classList.add('tc-loading');
    const message = form?.querySelector(SELECTORS.message);
    if (message) message.textContent = '';
    try {
      const response = await fetch(routes().change, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: lineElement.dataset.key, quantity })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
      const wasDrawerOpen = getDrawer()?.classList.contains('is-open');
      await refreshCart();
      if (wasDrawerOpen) {
        const drawer = getDrawer();
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        const updatedLine = drawer.querySelector(`[data-key="${CSS.escape(lineElement.dataset.key)}"]`);
        updatedLine?.querySelector(focusHint)?.focus();
      } else if (form?.closest('.tc-cart-page')) {
        window.location.reload();
      }
      document.dispatchEvent(new CustomEvent('two-cart:updated', { detail: data }));
    } catch (error) {
      if (message) message.textContent = error.message;
    } finally {
      form?.classList.remove('tc-loading');
      state.updating = false;
    }
  }

  function initRecommendations(scope = document) {
    scope.querySelectorAll('[data-tc-recommendations]').forEach(async (container) => {
      const productId = container.dataset.productId;
      const limit = container.dataset.limit || '8';
      if (!productId || container.dataset.load === 'false' || container.dataset.loaded === 'true') return;
      if (container.dataset.loading === 'true') return;
      container.dataset.loading = 'true';
      try {
        const sectionId = container.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '';
        const url = new URL(routes().recommendations, window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', limit);
        url.searchParams.set('intent', container.dataset.intent || 'related');
        if (sectionId) url.searchParams.set('section_id', sectionId);
        const response = await fetch(url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.querySelector(`[data-tc-recommendations][data-product-id="${CSS.escape(productId)}"][data-limit="${CSS.escape(limit)}"]`);
        const cards = replacement?.querySelector('[data-tc-recommendations-track]')?.innerHTML.trim();
        if (cards) container.querySelector('[data-tc-recommendations-track]').innerHTML = cards;
        container.dataset.loaded = 'true';
      } catch (error) {
        // Fallback collection/placeholder cards remain visible.
      } finally {
        container.dataset.loading = 'false';
      }
    });
  }

  document.addEventListener('click', (event) => {
    const drawerOpen = event.target.closest('[data-tc-cart-open]');
    if (drawerOpen) {
      event.preventDefault();
      openPanel(getDrawer(), drawerOpen);
      return;
    }
    const close = event.target.closest('[data-tc-close]');
    if (close) {
      event.preventDefault();
      closePanel(close.closest('.tc-drawer, .tc-measure-panel'));
      return;
    }
    if (event.target.closest(SELECTORS.overlay)) {
      closeAllPanels();
      return;
    }
    const quantityButton = event.target.closest('[data-tc-quantity]');
    if (quantityButton) {
      const line = quantityButton.closest('[data-tc-cart-line]');
      const input = line?.querySelector('[data-tc-quantity-input]');
      if (!input) return;
      const delta = quantityButton.dataset.tcQuantity === 'plus' ? 1 : -1;
      const nextQuantity = Math.max(0, Number(input.value) + delta);
      if (line.closest('.tc-drawer')) changeLine(line, nextQuantity, `[data-tc-quantity="${quantityButton.dataset.tcQuantity}"]`);
      else input.value = nextQuantity;
      return;
    }
    const remove = event.target.closest('[data-tc-remove]');
    if (remove) {
      event.preventDefault();
      changeLine(remove.closest('[data-tc-cart-line]'), 0);
      return;
    }
    const update = event.target.closest('[data-tc-cart-update]');
    if (update) {
      const form = update.closest('[data-tc-cart-form]');
      const lines = Array.from(form.querySelectorAll('[data-tc-cart-line]'));
      const updates = {};
      lines.forEach((line) => { updates[line.dataset.key] = Number(line.querySelector('[data-tc-quantity-input]')?.value || 0); });
      if (form.closest('.tc-cart-page')) form.requestSubmit();
      else {
        event.preventDefault();
        fetch(routes().update, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ updates }) })
          .then((response) => { if (!response.ok) throw new Error('Unable to update cart.'); return refreshCart({ open: true, trigger: update }); })
          .catch(() => { const message = form.querySelector(SELECTORS.message); if (message) message.textContent = 'Unable to update cart.'; });
      }
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-tc-quantity-input]');
    if (input && input.closest('.tc-drawer')) changeLine(input.closest('[data-tc-cart-line]'), Math.max(0, Number(input.value)), '[data-tc-quantity-input]');
  });

  document.addEventListener('keydown', (event) => {
    const activePanel = document.querySelector('.tc-drawer.is-open, .tc-measure-panel.is-open');
    if (!activePanel) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      closePanel(activePanel);
    } else trapFocus(event, activePanel);
  });

  function initialize(scope = document) {
    if (scope.matches?.('[data-tc-product]')) initProduct(scope);
    scope.querySelectorAll('[data-tc-product]').forEach(initProduct);
    if (scope.matches?.('[data-tc-recommendations]')) initRecommendations(scope.parentElement || document);
    initRecommendations(scope);
  }

  window.TwoCartSections = { initialize, refreshCart, openCart: (trigger) => openPanel(getDrawer(), trigger) };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => event.target.querySelectorAll('[data-tc-product]').forEach((root) => root.tcAbortController?.abort()));
})();
